import { FactoryProvider, InjectionToken } from '@angular/core';
import { Interceptor } from './interceptors-types';
import { InitThreadResponse, PromptResponse, SuggestionsResponse } from '../endpoint-contracts';

export function providePromptResponseInterceptor(factory: PromptInterceptorFactory): FactoryProvider {
    return { provide: PROMPT_RESPONSE_INTERCEPTORS, useFactory: factory, multi: true };
}

export function providePromptRequestInterceptor(factory: PromptInterceptorFactory): FactoryProvider {
    return { provide: PROMPT_REQUEST_INTERCEPTORS, useFactory: factory, multi: true };
}

export function provideInitThreadResponseInterceptor(factory: InitThreadInterceptorFactory): FactoryProvider {
    return { provide: INIT_THREAD_RESPONSE_INTERCEPTORS, useFactory: factory, multi: true };
}

export function provideSuggestionsResponseInterceptor(factory: SuggestionsInterceptorFactory): FactoryProvider {
    return { provide: SUGGESTIONS_RESPONSE_INTERCEPTORS, useFactory: factory, multi: true };
}

export function provideStreamingPromptResponseInterceptor(factory: PromptInterceptorFactory): FactoryProvider {
    return { provide: STREAMING_PROMPT_RESPONSE_INTERCEPTORS, useFactory: factory, multi: true };
}

/**
 * A token used to provide PromptResponse interceptors.
 *
 * Prefer using {@link providePromptResponseInterceptor} to provide prompt interceptors instead of specifying this token directly.
 */
export const PROMPT_RESPONSE_INTERCEPTORS = new InjectionToken('__PROMPT_RESPONSE_INTERCEPTORS__', {
    factory: (): Interceptor<PromptResponse>[] => []
});

/**
 * A token used to provide prompt requests messages interceptors.
 *
 * Prefer using {@link providePromptRequestInterceptor} to provide prompt interceptors instead of specifying this token directly.
 */
export const PROMPT_REQUEST_INTERCEPTORS = new InjectionToken('PROMPT_REQUEST_INTERCEPTORS', {
    factory: (): Interceptor<PromptResponse>[] => []
});

/**
 * A token used to provide InitThreadResponse interceptors.
 *
 * Prefer using {@link provideInitThreadResponseInterceptor} to provide interceptors instead of specifying this token directly.
 */
export const INIT_THREAD_RESPONSE_INTERCEPTORS = new InjectionToken('__INIT_THREAD_RESPONSE_INTERCEPTORS__', {
    factory: (): Interceptor<InitThreadResponse>[] => []
});

/**
 * A token used to provide SuggestionsResponse interceptors.
 *
 * Prefer using {@link provideSuggestionsResponseInterceptor} to provide interceptors instead of specifying this token directly.
 */
export const SUGGESTIONS_RESPONSE_INTERCEPTORS = new InjectionToken('__SUGGESTIONS_RESPONSE_INTERCEPTORS__', {
    factory: (): Interceptor<SuggestionsResponse>[] => []
});

/**
 * A token used to provide PromptResponse interceptors that run on every poll (including intermediate ones).
 * Use this for interceptors that need to observe streaming events such as `session_init`, `tool_start`, and `tool_complete`.
 *
 * Prefer using {@link provideStreamingPromptResponseInterceptor} to provide interceptors instead of specifying this token directly.
 */
export const STREAMING_PROMPT_RESPONSE_INTERCEPTORS = new InjectionToken('__STREAMING_PROMPT_RESPONSE_INTERCEPTORS__', {
    factory: (): Interceptor<PromptResponse>[] => []
});

type PromptInterceptorFactory = (...deps: any[]) => Interceptor<PromptResponse>;
type InitThreadInterceptorFactory = (...deps: any[]) => Interceptor<InitThreadResponse>;
type SuggestionsInterceptorFactory = (...deps: any[]) => Interceptor<SuggestionsResponse>;
